import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { exhibitionColumnsVisibility } from "@/config/dashboard/exhibition";
import { multipleprintsLinksColumnsVisibility } from "@/config/dashboard/multipleprints";
import { FormLayoutElement } from "@/config/forms/general";
import { computeFieldMultipleprintRelatedAuthors } from "@/lib/computeFields";
import {
  Exhibition,
  Multipleprint,
  MultipleprintInExhibition,
} from "@/lib/types";
import i18n, { SupportedLocale } from "@/i18n";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { buildElColumns } from "./elColumnComposer";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  ExhibitionLinkGridCard,
  MultipleprintLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import MultipleprintInExhibitionDrawer from "./drawer/MultipleprintInExhibitionDrawer";
import { ComplexItinerantGate } from "./drawer/ComplexItinerantGate";

interface ComplexLinkProps {
  id: string;
  exhibition?: Exhibition;
  multipleprint?: Multipleprint;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

type MultipleprintInExhibitionLinksProps =
  LinksTableProps<MultipleprintInExhibition> & {
    multipleprint?: Multipleprint;
  };

type ExhibitionToMultipleprintLinksProps =
  LinksTableProps<MultipleprintInExhibition> & {
    exhibition?: Exhibition;
  };

/**
 * MultipleprintInExhibtionLinks component
 * @returns {JSX.Element}
 */
/** Colonnes quand on est sur une exposition : mêmes colonnes que MultipleLinks (œuvre → multiples). */
function renderMultipleprintInExhibitionRowActionsWhenExhibition(
  data: MultipleprintInExhibition,
  t: (key: string) => string,
  handleDeleteRelation: (id: string) => void,
  fields: FormLayoutElement[] | undefined,
  id: string,
  type: LinkableType,
) {
  if (!data.multipleprint) return null;
  return (
    <LinkDrawerRowActions
      previewType="multipleprint"
      previewId={data.multipleprint.id}
      previewElements={[
        {
          label: t("estampesMultiplesDescriptionNumeroEnregistrement"),
          value: data.multipleprint.estampesMultiplesDescriptionNumeroEnregistrement,
          type: "text",
        },
        {
          label: t("estampesMultiplesDescriptionResume"),
          value: data.multipleprint.estampesMultiplesDescriptionResume,
          type: "richtext",
        },
        {
          label: t("attachments"),
          value: data.multipleprint.attachments,
          type: "image",
        },
      ]}
      onDelete={() => handleDeleteRelation(data.id)}
      editDrawer={({ open, onOpenChange }) => (
        <MultipleprintInExhibitionDrawer
          key={data.id}
          fields={fields}
          data={data}
          queryId={id}
          type={type}
          open={open}
          onOpenChange={onOpenChange}
        />
      )}
    />
  );
}

function renderMultipleprintInExhibitionRowActionsWhenMultipleprint(
  data: MultipleprintInExhibition,
  t: (key: string) => string,
  handleDeleteRelation: (id: string) => void,
  fields: FormLayoutElement[] | undefined,
  id: string,
  type: LinkableType,
  allLinks?: MultipleprintInExhibition[],
) {
  if (!data.exhibition) return null;
  return (
    <LinkDrawerRowActions
      previewType="exhibition"
      previewId={data.exhibition.id}
      previewElements={[
        {
          label: t("expositionsDescriptionNumeroExpo"),
          value: data.exhibition.expositionsDescriptionNumeroExpo,
          type: "text",
        },
        {
          label: t("expositionsDescriptionResume"),
          value: data.exhibition.expositionsDescriptionResume,
          type: "richtext",
        },
      ]}
      onDelete={() => handleDeleteRelation(data.id)}
      editDrawer={({ open, onOpenChange }) => (
        <ComplexItinerantGate
          open={open}
          onOpenChange={onOpenChange}
          parentType="multipleprint"
          parentId={id}
          exhibition={data.exhibition}
          existingLink={data}
          allLinks={allLinks}
          complexRelationKey="multipleprintInExhibition"
          getCatalogNumber={(l) =>
            l.expositionsDansEstampesMultiplesDescriptionNumero
          }
          getExemplaireExpose={(l) =>
            l.expositionsDansEstampesMultiplesDescriptionExemplaireExpose
          }
          showExemplaireExpose
          classic={
            <MultipleprintInExhibitionDrawer
              key={data.id}
              fields={fields}
              data={data}
              queryId={id}
              type={type}
              open={open}
              onOpenChange={onOpenChange}
            />
          }
        />
      )}
    />
  );
}

function columnsWhenExhibition(
  t: (key: string) => string,
  handleDeleteRelation: (id: string) => void,
  fields: FormLayoutElement[] | undefined,
  id: string,
  type: LinkableType,
): ColumnDef<MultipleprintInExhibition, any>[] {
  const targetColumns: ColumnDef<MultipleprintInExhibition, any>[] = [
    {
      accessorKey: "estampesMultiplesDescriptionNumeroEnregistrement",
      header: t("estampesMultiplesDescriptionNumeroEnregistrement"),
      cell: ({ row }: { row: Row<MultipleprintInExhibition> }) => (
        <div className="flex items-center gap-2 text-nowrap">
          <p className="text-[14px] font-[500]">
            {row.original.multipleprint
              ?.estampesMultiplesDescriptionNumeroEnregistrement}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "estampesMultiplesDescriptionVignette",
      header: "",
      size: 100,
      cell: ({ row }) => (
        <ImageCell
          value=""
          attachments={row.original.multipleprint?.attachments ?? []}
          domain="multipleprint"
        />
      ),
    },
    {
      accessorKey: "estampesMultiplesDescriptionTitre",
      header: t("estampesMultiplesDescriptionTitre"),
      size: 200,
      cell: ({ row }) => (
        <div className="flex items-center gap-2 text-wrap">
          <p className="text-[14px] font-[500]">
            {row.original.multipleprint?.estampesMultiplesDescriptionTitre}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "author",
      header: t("tab_relatedAuthor"),
      size: 200,
      cell: ({ row }) => {
        const mp = row.original.multipleprint;
        if (!mp) return null;
        const authors = computeFieldMultipleprintRelatedAuthors(mp, {
          language: i18n.language as SupportedLocale,
        });
        return <TextCell value={authors} />;
      },
    },
    {
      accessorKey: "estampesMultiplesDescriptionDateDeCreation",
      header: t("tab_estampesMultiplesDescriptionDateDeCreation"),
      size: 200,
      cell: ({ row }) => (
        <TextCell
          value={
            row.original.multipleprint
              ?.estampesMultiplesDescriptionDateDeCreation
          }
        />
      ),
    },
    {
      accessorKey: "estampesMultiplesDescriptionResume",
      header: t("estampesMultiplesDescriptionResume"),
      size: 300,
      cell: ({ row }) => (
        <HtmlCell
          value={
            row.original.multipleprint?.estampesMultiplesDescriptionResume ?? ""
          }
        />
      ),
    },
  ];

  const transitionColumns: ColumnDef<MultipleprintInExhibition, any>[] = [
    {
      accessorKey: "expositionsDansEstampesMultiplesDescriptionNumero",
      header: t("expositionsDansEstampesMultiplesDescriptionNumero"),
      cell: ({ row }) => (
        <TextCell
          value={row.original.expositionsDansEstampesMultiplesDescriptionNumero}
        />
      ),
    },
    {
      accessorKey: "expositionsDansEstampesMultiplesDescriptionExemplaireExpose",
      header: t("expositionsDansEstampesMultiplesDescriptionExemplaireExpose"),
      cell: ({ row }) => (
        <TextCell
          value={
            row.original
              .expositionsDansEstampesMultiplesDescriptionExemplaireExpose
          }
        />
      ),
    },
  ];

  const actionsColumn = createActionsColumnDef<MultipleprintInExhibition>(
    ({ row }: { row: Row<MultipleprintInExhibition> }) =>
      renderMultipleprintInExhibitionRowActionsWhenExhibition(
        row.original,
        t,
        handleDeleteRelation,
        fields,
        id,
        type,
      ),
  );
  return buildElColumns(targetColumns, transitionColumns, actionsColumn);
}

/** Colonnes quand on est sur un multiple : afficher les expositions liées (infos exhibition). */
function columnsWhenMultipleprint(
  t: (key: string) => string,
  handleDeleteRelation: (id: string) => void,
  fields: FormLayoutElement[] | undefined,
  id: string,
  type: LinkableType,
  allLinks?: MultipleprintInExhibition[],
): ColumnDef<MultipleprintInExhibition, any>[] {
  const targetColumns: ColumnDef<MultipleprintInExhibition, any>[] = [
    {
      accessorKey: "exhibition.expositionsDescriptionNumeroExpo",
      header: t("expositionsDescriptionNumeroExpo"),
      cell: ({ row }: { row: Row<MultipleprintInExhibition> }) => (
        <TextCell
          value={row.original.exhibition?.expositionsDescriptionNumeroExpo}
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionVignette",
      header: "",
      size: 100,
      cell: ({ row }) => (
        <ImageCell
          value=""
          attachments={row.original.exhibition?.attachments ?? []}
          domain="exhibition"
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionAnnee",
      header: t("expositionsDescriptionAnnee"),
      size: 10,
      cell: ({ row }) => (
        <TextCell
          value={row.original.exhibition?.expositionsDescriptionAnnee}
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionTitre",
      header: t("expositionsDescriptionTitre"),
      size: 300,
      cell: ({ row }) => (
        <HtmlCell
          value={row.original.exhibition?.expositionsDescriptionTitre ?? ""}
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionDates",
      header: t("expositionsDescriptionDates"),
      cell: ({ row }) => (
        <TextCell
          value={row.original.exhibition?.expositionsDescriptionDates}
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionLieu",
      header: t("expositionsDescriptionLieu"),
      size: 300,
      cell: ({ row }) => (
        <div className="flex flex-col items-start text-wrap">
          <p className="text-[14px] font-[500]">
            {row.original.exhibition?.expositionsDescriptionVille}
          </p>
          <p className="text-[14px] font-[400] text-gray-500">
            {row.original.exhibition?.expositionsDescriptionLieu}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionVille",
      header: t("expositionsDescriptionVille"),
      cell: ({ row }) => (
        <TextCell value={row.original.exhibition?.expositionsDescriptionVille} />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionPays",
      header: t("expositionsDescriptionPays"),
      cell: ({ row }) => (
        <TextCell value={row.original.exhibition?.expositionsDescriptionPays} />
      ),
    },
    {
      accessorKey: "exhibition.expositionsPublicationStatus",
      header: t("expositionsPublicationStatus"),
      cell: ({ row }) => (
        <TextCell value={row.original.exhibition?.expositionsPublicationStatus} />
      ),
    },
  ];

  const transitionColumns: ColumnDef<MultipleprintInExhibition, any>[] = [
    {
      accessorKey: "expositionsDansEstampesMultiplesDescriptionNumero",
      header: t("expositionsDansEstampesMultiplesDescriptionNumero"),
      cell: ({ row }: { row: Row<MultipleprintInExhibition> }) => (
        <TextCell
          value={row.original.expositionsDansEstampesMultiplesDescriptionNumero}
        />
      ),
    },
    {
      accessorKey: "expositionsDansEstampesMultiplesDescriptionExemplaireExpose",
      header: t("expositionsDansEstampesMultiplesDescriptionExemplaireExpose"),
      cell: ({ row }: { row: Row<MultipleprintInExhibition> }) => (
        <TextCell
          value={
            row.original.expositionsDansEstampesMultiplesDescriptionExemplaireExpose
          }
        />
      ),
    },
  ];

  const actionsColumn = createActionsColumnDef<MultipleprintInExhibition>(
    ({ row }: { row: Row<MultipleprintInExhibition> }) =>
      renderMultipleprintInExhibitionRowActionsWhenMultipleprint(
        row.original,
        t,
        handleDeleteRelation,
        fields,
        id,
        type,
        allLinks,
      ),
  );
  return buildElColumns(targetColumns, transitionColumns, actionsColumn);
}

const MultipleprintInExhibtionLinks = ({
  id,
  exhibition,
  multipleprint,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "multipleprintInExhibition",
    type,
  );

  const { mutate: deleteMultipleprintInExhibition } = useDeleteComplexLink(
    id,
    "multipleprintInExhibition",
    type,
  );

  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (relationId: string) => {
      deleteMultipleprintInExhibition({
        id: relationId,
        relation: "multipleprintInExhibition",
      });
    },
    [deleteMultipleprintInExhibition],
  );

  const renderMultipleprintInExhibitionRowActions = useCallback(
    (data: MultipleprintInExhibition) =>
      type === "exhibition"
        ? renderMultipleprintInExhibitionRowActionsWhenExhibition(
            data,
            t,
            handleDeleteRelation,
            fields,
            id,
            type,
          )
        : renderMultipleprintInExhibitionRowActionsWhenMultipleprint(
            data,
            t,
            handleDeleteRelation,
            fields,
            id,
            type,
            relatedData,
          ),
    [fields, handleDeleteRelation, id, t, type, relatedData],
  );

  const columns = useMemo(
    () =>
      type === "exhibition"
        ? columnsWhenExhibition(
            t,
            handleDeleteRelation,
            fields,
            id,
            type,
          )
        : columnsWhenMultipleprint(
            t,
            handleDeleteRelation,
            fields,
            id,
            type,
            relatedData,
          ),
    [fields, handleDeleteRelation, id, t, type, relatedData],
  );

  return (
    <div className="w-full">
      {type === "exhibition" ? (
        <ExhibitionToMultipleprintLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          exhibition={exhibition}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderMultipleprintInExhibitionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedMultipleprints")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      ) : (
        <MultipleprintToExhibitionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          multipleprint={multipleprint}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderMultipleprintInExhibitionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedExhibitions")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      )}
    </div>
  );
};

/** Vue exposition : liste des multiples liés à cette exposition ; en mode ajout, multiples pas encore liés. */
const ExhibitionToMultipleprintLinks = ({
  id,
  relatedData,
  fields,
  exhibition,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: ExhibitionToMultipleprintLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const STORAGE_KEY_ADD = "multipleprint-exhibition-add";
  const STORAGE_KEY_LINKED = "multipleprint-exhibition-linked";

  const defaultVisibilityLinked: VisibilityState = {
    "estampesMultiplesDescriptionNumeroEnregistrement": true,
    vignette: true,
    "estampesMultiplesDescriptionTitre": true,
    multipleprintAuthor: true,
    "estampesMultiplesDescriptionDateDeCreation": true,
    expositionsDansEstampesMultiplesDescriptionNumero: true,
    expositionsDansEstampesMultiplesDescriptionExemplaireExpose: true,
    actions: true,
  };

  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...multipleprintsLinksColumnsVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...multipleprintsLinksColumnsVisibility };
    });

  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityLinked, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityLinked };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const { data: notAlreadyRelatedMultipleprints } =
    useGetNotRelatedResource<Multipleprint>({
      resultType: "multiple",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsMultipleprints: ColumnDef<Multipleprint, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "estampesMultiplesDescriptionNumeroEnregistrement",
        header: t("estampesMultiplesDescriptionNumeroEnregistrement"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2 text-nowrap">
            <p className="text-[14px] font-[500]">
              {row.original.estampesMultiplesDescriptionNumeroEnregistrement}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "estampesMultiplesDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="multipleprint"
          />
        ),
      },
      {
        accessorKey: "estampesMultiplesDescriptionTitre",
        header: t("estampesMultiplesDescriptionTitre"),
        size: 200,
        cell: ({ row }) => (
          <div className="flex items-center gap-2 text-wrap">
            <p className="text-[14px] font-[500]">
              {row.original.estampesMultiplesDescriptionTitre}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "author",
        header: t("tab_relatedAuthor"),
        size: 200,
        cell: ({ row }) => {
          const authors = computeFieldMultipleprintRelatedAuthors(row.original, {
            language: i18n.language as SupportedLocale,
          });
          return <TextCell value={authors} />;
        },
      },
      {
        accessorKey: "estampesMultiplesDescriptionDateDeCreation",
        header: t("tab_estampesMultiplesDescriptionDateDeCreation"),
        size: 200,
        cell: ({ row }) => (
          <TextCell
            value={row.original.estampesMultiplesDescriptionDateDeCreation}
          />
        ),
      },
      createActionsColumnDef<Multipleprint>(({ row }) => {
          if (!exhibition) return null;
          return (
            <LinkDrawerRowActions
              previewType="multipleprint"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("estampesMultiplesDescriptionNumeroEnregistrement"),
                  value: row.original.estampesMultiplesDescriptionNumeroEnregistrement,
                  type: "text",
                },
                {
                  label: t("estampesMultiplesDescriptionResume"),
                  value: row.original.estampesMultiplesDescriptionResume,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultipleprintInExhibitionDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type="exhibition"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    exhibition,
                    multipleprint: row.original,
                    expositionsDansEstampesMultiplesDescriptionNumero: "",
                    expositionsDansEstampesMultiplesDescriptionExemplaireExpose:
                      "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [exhibition, fields, id, t]);

  const paginatedRelatedData = useMemo(() => {
    const start =
      relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: relatedData?.length ?? 0,
  };

  const addModeTable = (
    <DataTable
      columns={columnsMultipleprints}
      data={notAlreadyRelatedMultipleprints?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedMultipleprints?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <MultipleprintLinkGridCard
                    item={row.multipleprint}
                    t={t}
                    onTextClick={() => setSelectedRelationId(row.id)}
                  />
                </GridCardWithActions>
                <MultipleprintInExhibitionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="exhibition"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

const MultipleprintToExhibitionLinks = ({
  id,
  relatedData,
  fields,
  multipleprint,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: MultipleprintInExhibitionLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_EXH = "multipleprint-links-exhibition-add";
  const STORAGE_KEY_LINKED_EXH = "multipleprint-links-exhibition-linked";
  const defaultVisibilityExh = {
    ...exhibitionColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAddExh, setColumnVisibilityAddExh] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_EXH);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityExh, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityExh };
    });
  const [columnVisibilityLinkedExh, setColumnVisibilityLinkedExh] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_EXH);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityExh, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityExh };
    });
  const [columnsOrderAddExh, setColumnsOrderAddExh] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_EXH);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedExh, setColumnsOrderLinkedExh] =
    useState<string[]>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_EXH);
        if (saved) {
          const { order } = saved;
          return order || [];
        }
      } catch {
        // ignore
      }
      return [];
    });

  const { data: notAlreadyRelatedExhibitions } =
    useGetNotRelatedResource<Exhibition>({
      resultType: "exhibition",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsExhibitions: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "expositionsDescriptionNumeroExpo",
        header: t("expositionsDescriptionNumeroExpo"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.expositionsDescriptionNumeroExpo} />
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            row.original.attachments && (
              <ImageCell
                value={"expositionsDescriptionVignette"}
                attachments={row.original.attachments}
                domain={"exhibition"}
              />
            )
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionAnnee",
        header: t("expositionsDescriptionAnnee"),
        size: 10,
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionAnnee} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionTitre",
        header: t("expositionsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.expositionsDescriptionTitre} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionDates",
        header: t("expositionsDescriptionDates"),
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionDates} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionLieu",
        header: t("expositionsDescriptionLieu"),
        size: 300,
        cell: ({ row }) => {
          return (
            <div className="flex flex-col items-start text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.expositionsDescriptionVille}
              </p>
              <p className="text-[14px] font-[400] text-gray-500">
                {row.original.expositionsDescriptionLieu}
              </p>
            </div>
          );
        },
      },
      createActionsColumnDef<Exhibition>(({ row }) => {
          const data = row.original as Exhibition;
          if (!multipleprint) return null;
          return (
            <LinkDrawerRowActions
              previewType="exhibition"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("expositionsDescriptionNumeroExpo"),
                  value: data.expositionsDescriptionNumeroExpo,
                  type: "text",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <ComplexItinerantGate
                  key={row.original.id}
                  open={open}
                  onOpenChange={onOpenChange}
                  parentType="multipleprint"
                  parentId={id}
                  exhibition={data}
                  allLinks={relatedData}
                  complexRelationKey="multipleprintInExhibition"
                  getCatalogNumber={(l) =>
                    l.expositionsDansEstampesMultiplesDescriptionNumero
                  }
                  getExemplaireExpose={(l) =>
                    l.expositionsDansEstampesMultiplesDescriptionExemplaireExpose
                  }
                  showExemplaireExpose
                  classic={
                    <MultipleprintInExhibitionDrawer
                      fields={fields}
                      queryId={id}
                      type={"multipleprint"}
                      open={open}
                      onOpenChange={onOpenChange}
                      data={{
                        id: "",
                        exhibition: data,
                        multipleprint: multipleprint,
                        expositionsDansEstampesMultiplesDescriptionNumero: "",
                        expositionsDansEstampesMultiplesDescriptionExemplaireExpose:
                          "",
                      }}
                    />
                  }
                />
              )}
            />
          );
        }),
    ];
  }, [fields, id, multipleprint, t, relatedData]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: relatedData?.length ?? 0,
  };
  const addModeTable = (
    <DataTable
      columns={columnsExhibitions}
      data={notAlreadyRelatedExhibitions?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedExhibitions?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD_EXH}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAddExh,
        onColumnOrderChange: setColumnsOrderAddExh,
        state: {
          columnVisibility: columnVisibilityAddExh,
          columnOrder: columnsOrderAddExh,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED_EXH}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinkedExh,
        onColumnOrderChange: setColumnsOrderLinkedExh,
        state: {
          columnVisibility: columnVisibilityLinkedExh,
          columnOrder: columnsOrderLinkedExh,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_EXH}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <ExhibitionLinkGridCard
                    item={row.exhibition}
                    t={t}
                    onTextClick={() => setSelectedRelationId(row.id)}
                  />
                </GridCardWithActions>
                <MultipleprintInExhibitionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="multipleprint"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default MultipleprintInExhibtionLinks;
